import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useAuth0 } from './../../contexts/auth0-context'
import callAPI from './../../API/callSecureAPI'
import endpoints from './../../API/endpoints'
import { KES } from './useGetDraftKES'

type UseSaveKESStatementsMutationProps = {
    onSuccess?: () => void
    onError?: (error: unknown) => void
}

type SaveKESVariables = {
    draftId: string
    localKES: KES[]
}

export const useSaveKESStatementsMutation = ({ onSuccess, onError }: UseSaveKESStatementsMutationProps = {}) => {
    const { getTokenSilently, isAuthenticated } = useAuth0()
    const queryClient = useQueryClient()

    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)
    // @ts-expect-error store is not typed properly
    const organizationId = useSelector((state) => state.strategy?.organizationId)

    const saveKESStatements = async ({ draftId, localKES }: SaveKESVariables) => {
        if (!localKES.length) return

        if (!isAuthenticated) throw new Error('User must be authenticated to save KES statements')

        const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
        if (!token) throw new Error('Failed to get authentication token')

        const response = await callAPI(
            endpoints.handleKeyEvidenceStatements.url,
            endpoints.handleKeyEvidenceStatements.method,
            token,
            {
                statements: localKES.map((k) => ({
                    ...(k._id ? { _id: k._id } : {}),
                    statement: k.statement,
                    figures: k.figures,
                    status: 'approved',
                    parentDraft: draftId,
                    strategyId,
                    organizationId,
                })),
                deleteIds: [],
            }
        )

        if (!response.success) throw new Error(response.error || 'Failed to save KES statements')

        return response
    }

    return useMutation(saveKESStatements, {
        onSuccess: (_data, { draftId }) => {
            queryClient.invalidateQueries(['draftKES', draftId])
            onSuccess?.()
        },
        onError: (error) => {
            console.error('Error saving KES statements:', error)
            onError?.(error)
        },
    })
}
